import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Download, 
  Eye, 
  Calendar,
  Users,
  Filter,
  FileSpreadsheet,
  TrendingUp,
  Award,
  Building
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const InterfaceRelatorios = ({ pdiId, colaboradorId }) => {
  const [loading, setLoading] = useState(false);
  const [pdis, setPdis] = useState([]);
  const [filtros, setFiltros] = useState({
    area: 'todas',
    periodo_inicio: '',
    periodo_fim: ''
  });

  useEffect(() => {
    if (!pdiId) {
      loadPDIs();
    }
  }, []);

  const loadPDIs = async () => {
    try {
      const response = await axios.get(`${API}/api/pdi-avancado`);
      setPdis(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar PDIs:', error);
    }
  };

  const gerarRelatorioPDFIndividual = async (pdiIdParam) => {
    try {
      setLoading(true);
      toast.info('Gerando PDF...');

      const response = await axios.get(
        `${API}/api/pdi-completo/relatorios/pdf/${pdiIdParam || pdiId}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pdi_individual_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioExcelIndividual = async (pdiIdParam) => {
    try {
      setLoading(true);
      toast.info('Gerando Excel...');

      const response = await axios.get(
        `${API}/api/pdi-completo/relatorios/excel/${pdiIdParam || pdiId}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pdi_individual_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Excel gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar Excel:', error);
      toast.error('Erro ao gerar Excel');
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioExecutivo = async () => {
    try {
      setLoading(true);
      toast.info('Gerando relatório executivo...');

      const params = {};
      if (filtros.area !== 'todas') {
        params.area = filtros.area;
      }

      const response = await axios.get(
        `${API}/api/pdi-completo/relatorios/executivo`,
        { params }
      );

      // Exibir em modal ou nova aba
      console.log('Relatório Executivo:', response.data);
      
      // Download como JSON
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const link = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `relatorio_executivo_${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Relatório executivo gerado!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const tiposRelatorios = [
    {
      id: 'pdf_individual',
      titulo: 'PDF Individual',
      descricao: 'Relatório completo de um PDI específico',
      icon: FileText,
      color: 'bg-red-500',
      acao: gerarRelatorioPDFIndividual
    },
    {
      id: 'excel_individual',
      titulo: 'Excel Individual',
      descricao: 'Planilha detalhada de um PDI',
      icon: FileSpreadsheet,
      color: 'bg-green-500',
      acao: gerarRelatorioExcelIndividual
    },
    {
      id: 'executivo',
      titulo: 'Relatório Executivo',
      descricao: 'Visão estratégica para diretoria',
      icon: TrendingUp,
      color: 'bg-purple-500',
      acao: gerarRelatorioExecutivo
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-600 rounded-full">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">Central de Relatórios</h2>
              <p className="text-gray-600">
                Gere relatórios individuais, consolidados e executivos em PDF ou Excel
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      {!pdiId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Filter className="h-5 w-5" />
              <span>Filtros de Relatórios</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold mb-2 block">Área</label>
                <Select value={filtros.area} onValueChange={(value) => setFiltros({...filtros, area: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas as Áreas</SelectItem>
                    <SelectItem value="TI">TI</SelectItem>
                    <SelectItem value="RH">RH</SelectItem>
                    <SelectItem value="Comercial">Comercial</SelectItem>
                    <SelectItem value="Financeiro">Financeiro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-semibold mb-2 block">Data Início</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded-md"
                  value={filtros.periodo_inicio}
                  onChange={(e) => setFiltros({...filtros, periodo_inicio: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm font-semibold mb-2 block">Data Fim</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded-md"
                  value={filtros.periodo_fim}
                  onChange={(e) => setFiltros({...filtros, periodo_fim: e.target.value})}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tipos de Relatórios */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiposRelatorios.map(tipo => {
          const Icon = tipo.icon;
          
          return (
            <Card key={tipo.id} className="hover:shadow-lg transition-shadow cursor-pointer border-2">
              <CardContent className="p-6">
                <div className={`${tipo.color} w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto`}>
                  <Icon className="h-7 w-7 text-white" />
                </div>
                
                <h3 className="text-lg font-bold text-center mb-2">{tipo.titulo}</h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  {tipo.descricao}
                </p>

                <Button 
                  className="w-full"
                  onClick={() => tipo.acao()}
                  disabled={loading || (tipo.id !== 'executivo' && !pdiId && pdis.length === 0)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Gerar {tipo.titulo}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Lista de PDIs (se não houver pdiId específico) */}
      {!pdiId && pdis.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span>PDIs Disponíveis ({pdis.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pdis.slice(0, 10).map(pdi => (
                <Card key={pdi.id} className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold">{pdi.colaborador_nome || 'Colaborador'}</h4>
                          <Badge>{pdi.status}</Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          Progresso: {pdi.progresso_geral?.toFixed(0) || 0}%
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => gerarRelatorioPDFIndividual(pdi.id)}
                          disabled={loading}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          PDF
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => gerarRelatorioExcelIndividual(pdi.id)}
                          disabled={loading}
                        >
                          <FileSpreadsheet className="h-3 w-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Relatórios Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-orange-600" />
            <span>Relatórios Consolidados</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2">Relatório por Área</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Consolidado de todos os PDIs por departamento
                </p>
                <Button variant="outline" className="w-full" disabled={loading}>
                  <Download className="h-4 w-4 mr-2" />
                  Gerar por Área
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-2 border-green-200">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2">Relatório de Auditoria</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Trilha completa de ações e evidências
                </p>
                <Button variant="outline" className="w-full" disabled={loading}>
                  <Award className="h-4 w-4 mr-2" />
                  Gerar Auditoria
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InterfaceRelatorios;
